import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux/es/exports";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import swal from "sweetalert";
import AddButton from "../../components/AddButton";
import DeleteButton from "../../components/DeleteButton";
import EditButton from "../../components/EditButton";
import Pagination from "../../components/Pagination";
import SearchInputComponent from "../../components/SearchInputComponent";
import Select from "../../components/Select";
import TemDataTableInline from "../../components/TemDataTableInline";
import Loading from "../../components/loading";
import {
  deleteTextureById,
  fetchDataTexture,
} from "../../services/textureService";
import TextureMatching from "./TextureMatching";

const headerTable = [
  {
    name: "id",
    sort: false,
  },
  {
    name: "laminate",
    sort: false,
  },
  {
    name: "name",
    sort: true,
  },
  {
    name: "color",
    sort: true,
  },
  {
    name: "priority",
    sort: false,
  },
  <span className="sr-only">Edit</span>,
];

const bodyTable = ["id", "imageId", "name", "color","priority", "actions"];

const Textures = ({ t }) => {
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [itemOffset, setItemOffset] = useState(
    localStorage.getItem("itemOffset")
      ? parseInt(localStorage.getItem("itemOffset"))
      : null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    data: [],
    recordsTotal: 0,
    pageCount: 0,
  });
  const [forcePage, setForcePage] = useState(
    localStorage.getItem("forcePage")
      ? parseInt(localStorage.getItem("forcePage"))
      : 0
  );
  const { token } = useSelector((state) => state.user);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDir, setSortDir] = useState("DESC");
  const [clear, setClear] = useState(false);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");

  const oldSelectedId = localStorage.getItem("selectedId")
    ? parseInt(localStorage.getItem("selectedId"))
    : 0;
  const [selectedId, setSelectedId] = useState(oldSelectedId);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageClick = (event) => {
    setForcePage(event.selected);
    const newOffset =
      (event.selected * parseInt(itemsPerPage)) % data.recordsTotal;
    setItemOffset(newOffset);
  };

  const onDeleteItem = async (id) => {
    const responseReceived = await swal({
      title: t(`action.delete_item.title`),
      text: t(`action.delete_item.text`),
      icon: "warning",
      buttons: {
        cancel: true,
        confirm: true,
      },
    });
    if (responseReceived) {
      setIsLoading(true);
      const response = await deleteTextureById(
        `${process.env.REACT_APP_HOST_BASE_URL}/v1/texture/${id}`,
        token
      );
      if (response.success) {
        setIsLoading(false);
        fetchData();
        toast.success(t("action.delete_success"), {
          position: "bottom-right",
          autoClose: 3000,
          closeOnClick: true,
          className: "add-success",
        });
      } else {
        setIsLoading(false);
        if (response.status === 420) {
          toast.warn(response.body.error, {
            position: "bottom-right",
            autoClose: 10000,
            closeOnClick: true,
            className: "product-background",
            pauseOnHover: true,
          });
        } else {
          navigate("/error", {
            state: {
              status: response.status,
              message: response.body ? response.body.error : "Error",
            },
          });
        }
      }
    } else {
      return;
    }
  };
  const fetchData = async () => {
    const response = await fetchDataTexture(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/texture/table`,
      token,
      {
        draw: 1,
        start: itemOffset,
        length: itemsPerPage,
        recordsTotal: 0,
        columns: [],
        order: [{ column: sortColumn, dir: sortDir }],
        search: { value: searchTerm, regex: true },
        filter: {},
      }
    );
    if (response.success) {
      setIsLoading(false);
      setClear(false);
      let reviewData = response.data.data.map((item, index) => {
        return {
          ...item,
          color: (
            <div
              style={{ backgroundColor: `${item.color}` }}
              className={` w-full h-6 rounded-lg `}
            ></div>
          ),
          imageId: item.imageId ? (
            <img
              className="object-cover w-6 h-6 rounded-full"
              src={`${process.env.REACT_APP_HOST_BASE_URL}/v1/file/texture/${item.imageId}`}
              alt={"img"}
            />
          ) : (
            <p className=" text-[16px] font-bold ">{t(`no_laminate`)}</p>
          ),
          actions: (
            <>
              <EditButton
                onclick={() => navigate(`/textures/update/${item.id}`)}
              />
              <DeleteButton onclick={() => onDeleteItem(item.id)} />
            </>
          ),
        };
      });
      setData({
        data: reviewData,
        recordsTotal: response.data.recordsFiltered,
        pageCount: Math.ceil(
          response.data.recordsFiltered / parseInt(itemsPerPage)
        ),
      });
    } else {
      setIsLoading(false);
      navigate("/error", {
        state: {
          status: response.status,
          message: response.message ? response.message : null,
        },
      });
    }
  };

  useEffect(() => {
    if (clear) {
      fetchData();
    }
  }, [clear]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log("render");
    fetchData();
  }, [itemOffset, itemsPerPage, searchTerm]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const sortFunction = (value) => {
    setSortColumn(value.field ? value.field : "id");
    setSortDir(value.sort === true ? "ASC" : "DESC");
    setClear(true);
  };

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <div className="px-4 py-4 sm:px-6 lg:px-8">
          <div className="mb-2 sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-semibold text-gray-900">
                {t(`textures`)}
              </h1>
            </div>
            <AddButton title={t(`add_textures`)} url={"add"} />
          </div>

          <div>
            <div className={"flex justify-between items-center"}>
              <Select
                setItemsPerPage={setItemsPerPage}
                itemsPerPage={itemsPerPage}
              />
              <SearchInputComponent
                className={"w-[13%]"}
                handleSearch={handleSearch}
                searchTerm={searchTerm}
              />
            </div>
            <TemDataTableInline
              data={data.data}
              headerTable={headerTable}
              bodyContent={bodyTable}
              rowHandler={(id) => {
                if (id !== selectedId) {
                  setSelectedId(id);
                }
              }}
              sortFunction={sortFunction}
            />
            <Pagination
              handlePageClick={handlePageClick}
              pageCount={data.pageCount}
              forcePage={forcePage}
            />
          </div>

          {selectedId ? (
            <div className="px-4 py-4 sm:px-6 lg:px-8">
              <div>
                <TextureMatching selectedId={selectedId} />
              </div>
            </div>
          ) : (
            ""
          )}
        </div>
      )}
      ;
    </>
  );
};

export default withTranslation()(Textures);
